import type { DecisionAgentDomain } from '../decision-agent-platform';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/risk-claims/risk-claims.types.ts
// (RISK_CLAIMS_DATA_LAKE_CONTEXT_KIND, RISK_CLAIMS_DATA_LAKE_SCHEMA_VERSION) —
// the exact pair risk-claims.data-lake-source.ts queries.
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): 4 APPROVED,
// ingest-anchored connector-derived rows, latest approvedAt 2026-07-29,
// asOf 2026-06-30.
//
// WHY ONE ARTIFACT BACKS ALL FIVE KEYS (restating the engine, not asserting past
// it): the artifact's own evidenceRefs carry a single class
// (`risk_claims_register_snapshot`), but risk-claims.data-lake-source.ts's
// resolveWorkspaceEvidencePosture() publishes the union of that ref set AND
// `riskClaimsManifest.requiredState.map(stateKey)` — with the module's own
// comment stating "the approved register reconciles into every governed
// risk-claims evidence key (claims register, carrier-and-lane map, subrogation
// actuals, chart-of-accounts mapping, contract liability terms)". So the engine
// itself treats this one approved snapshot as satisfying all five. This contract
// restates that; it does not widen it.
//
// FRESHNESS NOTE (disclosed, #4449 lane 1): at 2026-08-08 the live asOf is 39
// days old. The four `monthly` keys carry a 45-day ceiling, so they read PRESENT
// today with six days of headroom and flip STALE on 2026-08-14 without a
// refresh. The `quarterly` contract_liability_terms key has ample headroom. This
// is a data-refresh dependency, not a contract defect.
export const RISK_CLAIMS_EVIDENCE_KEYS = {
  claimsRegister: 'approved_claims_register',
  claimsByCarrierLaneMap: 'claims_by_carrier_lane_map',
  contractLiabilityTerms: 'contract_liability_terms',
  subrogationRecoveryActuals: 'subrogation_recovery_actuals',
  chartOfAccountsMapping: 'approved_chart_of_accounts_mapping',
} as const;

type RiskClaimsEvidenceKey =
  (typeof RISK_CLAIMS_EVIDENCE_KEYS)[keyof typeof RISK_CLAIMS_EVIDENCE_KEYS];

export const RISK_CLAIMS_CONTEXT_KINDS = {
  governedSnapshot: 'decision_agent:risk_claims:governed_snapshot',
} as const;

export const RISK_CLAIMS_SCHEMA_VERSIONS = {
  governedSnapshot: 'risk_claims_governed_snapshot_v1',
} as const;

const NO_RISK_CLAIMS_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Mirrors risk-claims.manifest.ts requiredState exactly, key for key.
const RISK_CLAIMS_DOMAIN_BY_EVIDENCE_KEY: Readonly<
  Record<RiskClaimsEvidenceKey, DecisionAgentDomain>
> = {
  approved_claims_register: 'risk',
  claims_by_carrier_lane_map: 'risk',
  contract_liability_terms: 'legal',
  subrogation_recovery_actuals: 'risk',
  approved_chart_of_accounts_mapping: 'finance',
};

const RISK_CLAIMS_CADENCE_BY_EVIDENCE_KEY = {
  approved_claims_register: 'monthly',
  claims_by_carrier_lane_map: 'monthly',
  contract_liability_terms: 'quarterly',
  subrogation_recovery_actuals: 'monthly',
  approved_chart_of_accounts_mapping: 'monthly',
} as const;

const RISK_CLAIMS_REQUIRED_FOR_BY_EVIDENCE_KEY = {
  approved_claims_register: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
  claims_by_carrier_lane_map: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
  contract_liability_terms: ['scenario', 'decision_pack', 'ask_helix'],
  subrogation_recovery_actuals: ['monitoring', 'decision_pack', 'ask_helix'],
  approved_chart_of_accounts_mapping: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
} as const;

const riskClaimsEntry = (evidenceKey: RiskClaimsEvidenceKey, description: string) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: RISK_CLAIMS_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: RISK_CLAIMS_SCHEMA_VERSIONS.governedSnapshot,
    bindingPlaceholders: NO_RISK_CLAIMS_BINDING_PLACEHOLDERS,
    domain: RISK_CLAIMS_DOMAIN_BY_EVIDENCE_KEY[evidenceKey],
    freshnessCadence: RISK_CLAIMS_CADENCE_BY_EVIDENCE_KEY[evidenceKey],
    requiredFor: RISK_CLAIMS_REQUIRED_FOR_BY_EVIDENCE_KEY[evidenceKey],
    missingBehavior: 'block',
    description,
  });

export const RISK_CLAIMS_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'risk-claims',
  {
    claims_register: riskClaimsEntry(
      RISK_CLAIMS_EVIDENCE_KEYS.claimsRegister,
      'Approved claims register carried by the governed risk-claims snapshot; the loss, reserve, ' +
        'and recovery record every risk-claims read and scenario intent resolves from.',
    ),
    claims_by_carrier_lane_map: riskClaimsEntry(
      RISK_CLAIMS_EVIDENCE_KEYS.claimsByCarrierLaneMap,
      'Carrier-and-lane attribution of the approved claims register; backs by-carrier and ' +
        'by-lane claims decomposition and the carrier requalification scenario.',
    ),
    contract_liability_terms: riskClaimsEntry(
      RISK_CLAIMS_EVIDENCE_KEYS.contractLiabilityTerms,
      'Approved contract liability terms reconciled from the same governed register; backs the ' +
        'liability-cap scenario and the contract-liability diagnostic.',
    ),
    subrogation_recovery_actuals: riskClaimsEntry(
      RISK_CLAIMS_EVIDENCE_KEYS.subrogationRecoveryActuals,
      'Approved subrogation recovery actuals reconciled from the same governed register; backs ' +
        'recovery-options comparison and recovery reads.',
    ),
    chart_of_accounts_mapping: riskClaimsEntry(
      RISK_CLAIMS_EVIDENCE_KEYS.chartOfAccountsMapping,
      'Approved chart-of-accounts mapping reconciled from the same governed register; ties total ' +
        'cost of risk back to the governed P&L account taxonomy.',
    ),
  },
  {
    evidenceKeys: Object.values(RISK_CLAIMS_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
    contextKinds: Object.values(RISK_CLAIMS_CONTEXT_KINDS),
    schemaVersions: Object.values(RISK_CLAIMS_SCHEMA_VERSIONS),
    stateKeys: Object.values(RISK_CLAIMS_EVIDENCE_KEYS),
    bindingPlaceholders: NO_RISK_CLAIMS_BINDING_PLACEHOLDERS,
  },
);

export type RiskClaimsModuleEvidenceContract = typeof RISK_CLAIMS_MODULE_EVIDENCE_CONTRACT;
